import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TextField,
  Autocomplete,
  CircularProgress,
  Button,
  Popper
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useTheme } from '../../ThemeContext';
import { message } from 'antd';
import { createProvidersFromBulkData, revalidateProviderBulkData } from '../../utils/apiClient';
import ThemedTooltip from '../common/ThemedTooltip';
import { callAutomationService } from '../../services/callAutomationService';
import { getById as getFacilityById } from '../../services/facility-service';

const MemoizedRow = React.memo(({
  row,
  columns,
  theme,
  rowDepartments,
  facilities,
  facilityOptions,
  addressOptions,
  handleCellEdit,
  handleDeleteRow,
  handleFacilityChange,
  getCellValue,
  getErrorForField,
  formatFacilityAddress
}) => {
  const { isDark, textPrimary, brandOrange, cellBorder } = theme;

  const getFacilityIdForRow = () => {
    if (!row || !row.data) return null;

    const npi = getCellValue(row, { field: 'Facility Group NPI' });
    const address = getCellValue(row, { field: 'Facility Address' });

    if (!npi) return null;

    const matchingNpiFacilities = facilities.filter(f => f.value === npi);

    if (matchingNpiFacilities.length === 0) return null;
    
    if (matchingNpiFacilities.length === 1) {
      return matchingNpiFacilities[0].id;
    }

    if (address) {
      const foundFacility = matchingNpiFacilities.find(f => {
        const facilityAddress = f.location ? formatFacilityAddress(f.location) : '';
        return facilityAddress.toLowerCase().trim() === address.toLowerCase().trim();
      });

      if (foundFacility) {
        return foundFacility.id;
      }
    }
    return null; 
  };

  const facilityId = getFacilityIdForRow();
  const facilityNpiOption = facilityOptions.find(opt => opt.value === facilityId) || null;
  
  const rowNpi = getCellValue(row, { field: 'Facility Group NPI' });

  let filteredAddressOptions;
  if (!rowNpi) {
    filteredAddressOptions = addressOptions; // Show all if no NPI
  } else {
    filteredAddressOptions = facilities
      .filter(f => f.value === rowNpi)
      .map(f => ({
        label: formatFacilityAddress(f.location),
        value: f.id,
        npi: f.value,
      }))
      .filter(f => f.label);
  }

  const facilityAddressOption = filteredAddressOptions.find(opt => opt.value === facilityId) || null;

  return (
    <TableRow 
      key={row.rowIndex}
      sx={{
        '&:hover': {
          backgroundColor: 'inherit !important'
        }
      }}
    >
      {columns.map((col) => {
        const errorMsg = getErrorForField(row, col.field);
        const cellValue = getCellValue(row, col);

        if (col.field === 'actions') {
          return (
            <TableCell
              key={col.field}
              align="center"
              sx={{
                borderBottom: cellBorder,
                padding: '12px 16px',
              }}
            >
              <ThemedTooltip title="Delete row" placement="top">
                <IconButton
                  onClick={() => handleDeleteRow(row.rowIndex)}
                  size="small"
                  sx={{
                    color: brandOrange,
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(230, 62, 43, 0.15)' : 'rgba(230, 62, 43, 0.08)'
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ThemedTooltip>
            </TableCell>
          );
        }
        
        return (
            <TableCell
              key={col.field}
              sx={{
                color: textPrimary,
                fontWeight: 200,
                borderBottom: cellBorder,
                fontSize: '.9rem',
                backgroundColor: errorMsg ? (isDark ? 'rgba(230, 62, 43, 0.1)' : 'rgba(230, 62, 43, 0.05)') : 'transparent',
                padding: '12px 16px',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: errorMsg 
                    ? (isDark ? 'rgba(230, 62, 43, 0.15)' : 'rgba(230, 62, 43, 0.08)') 
                    : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)')
                }
              }}
            >
            {col.field === 'Facility Group NPI' ? (
              <ThemedTooltip title={errorMsg || ''} placement="top">
                <Autocomplete
                  options={facilityOptions}
                  getOptionLabel={(option) => option.npi || ''}
                  value={facilityNpiOption}
                  onChange={(event, newValue) => {
                    handleFacilityChange(row.rowIndex, newValue);
                  }}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  PopperComponent={(props) => <Popper {...props} style={{ width: 400 }} />}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.value}>
                      {option.label}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      error={!!errorMsg}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        style: {
                          fontSize: '.9rem',
                          color: errorMsg ? brandOrange : textPrimary,
                          fontWeight: errorMsg ? 400 : 200
                        },
                        endAdornment: (
                          <>
                            {false ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          color: errorMsg ? brandOrange : textPrimary
                        }
                      }}
                    />
                  )}
                />
              </ThemedTooltip>
            ) : col.field === 'Facility Address' ? (
              <ThemedTooltip title={errorMsg || ''} placement="top">
                <Autocomplete
                  options={filteredAddressOptions}
                  getOptionLabel={(option) => option.label || ''}
                  value={facilityAddressOption}
                  onChange={(event, newValue) => {
                    handleFacilityChange(row.rowIndex, newValue);
                  }}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      error={!!errorMsg}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        style: {
                          fontSize: '.9rem',
                          color: errorMsg ? brandOrange : textPrimary,
                          fontWeight: errorMsg ? 400 : 200
                        },
                        endAdornment: (
                          <>
                            {false ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          color: errorMsg ? brandOrange : textPrimary
                        }
                      }}
                    />
                  )}
                />
              </ThemedTooltip>
            ) : col.field === 'Department Name' ? (
              <Autocomplete
                options={(rowDepartments[row.rowIndex]?.departments || []).map(d => ({ label: d.name, value: d.name }))}
                getOptionLabel={(option) => option.label || ''}
                value={cellValue ? { label: cellValue, value: cellValue } : null}
                onChange={(event, newValue) => {
                  handleCellEdit(row.rowIndex, col.field, newValue ? newValue.value : '');
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                loading={rowDepartments[row.rowIndex]?.loading}
                disabled={!row.data['Facility Group NPI'] || rowDepartments[row.rowIndex]?.loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    error={!!errorMsg}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      style: {
                        fontSize: '.9rem',
                        color: errorMsg ? brandOrange : textPrimary,
                        fontWeight: errorMsg ? 400 : 200
                      },
                      endAdornment: (
                        <>
                          {rowDepartments[row.rowIndex]?.loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        color: errorMsg ? brandOrange : textPrimary
                      }
                    }}
                  />
                )}
              />
            ) : (
              <ThemedTooltip title={errorMsg || ''} placement="top">
                <TextField
                  value={cellValue}
                  onChange={(e) => handleCellEdit(row.rowIndex, col.field, e.target.value)}
                  variant="standard"
                  fullWidth
                  size="small"
                  error={!!errorMsg}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      fontSize: '.9rem',
                      color: errorMsg ? brandOrange : textPrimary,
                      fontWeight: errorMsg ? 400 : 200
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      color: errorMsg ? brandOrange : textPrimary
                    }
                  }}
                />
              </ThemedTooltip>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
});

const BulkUploadValidationTable = ({ validationData, onBack, onSuccess }) => {
  const themeContext = useTheme();
  const themeMode = themeContext?.themeMode || 'light';
  const currentTheme = themeContext?.currentTheme;
  
  const [tableData, setTableData] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [duplicates, setDuplicates] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [rowDepartments, setRowDepartments] = useState({});
  const fetchingDepartments = useRef(new Set());

  const formatFacilityAddress = (location) => {
    if (!location) return '';
    return `${location.streetAddress}, ${location.city}, ${location.state} ${location.zipcode}`;
  };

  // Theme variables matching StickyFlexTable
  const isDark = themeMode === 'dark';
  const mainBg = currentTheme?.callhistory?.mainBackground || (isDark ? '#04002b' : '#ffffff');
  const headerBg = currentTheme?.callhistory?.headerBackground || (isDark ? '#242143' : '#f5f5f5');
  const headerText = currentTheme?.text?.header || (isDark ? '#fff' : '#222');
  const textPrimary = currentTheme?.text?.primary || (isDark ? '#ffffff' : '#0b0731');
  const cellBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';
  const brandOrange = '#e63e2b';

  // Helper function to normalize data structure for consistent handling
  const normalizeRowData = (row) => {
    if (!row || !row.data) return row;
    
    // Normalize errors array to use display field names
    const normalizeErrors = (errors) => {
      if (!errors || !Array.isArray(errors)) return [];
      
      // Map API field names to display field names
      const apiToDisplayFieldMapping = {
        'fullName': 'FullName',
        'npi': 'NPI',
        'NPI': 'NPI',
        'taxId': 'TaxId',
        'TaxId': 'TaxId',
        'specialties': 'Specialties',
        'Specialties': 'Specialties',
        'affiliation.facilityId': 'Facility Group NPI',
        'facilityId': 'Facility Group NPI',
        'facilityGroupNpi': 'Facility Group NPI',
        'affiliation.facilityAddress': 'Facility Address',
        'facilityAddress': 'Facility Address',
        'affiliation.departmentId': 'Department Name',
        'departmentId': 'Department Name',
        'departmentName': 'Department Name',
        'affiliation.role': 'Roles',
        'role': 'Roles',
        'roles': 'Roles',
        'FullName': 'FullName',
        'Facility Group NPI': 'Facility Group NPI',
        'Facility Address': 'Facility Address',
        'Department Name': 'Department Name',
        'Roles': 'Roles',
        // Keep general errors as-is
        'general': 'general',
        'General': 'general'
      };
      
      return errors.map(err => ({
        ...err,
        field: apiToDisplayFieldMapping[err.field] || err.field
      }));
    };
    
    // Normalize errors
    const normalizedErrors = normalizeErrors(row.errors);
    
    // If data already has both formats, just update errors and return
    if (row.data.FullName && row.data.fullName) {
      return {
        ...row,
        errors: normalizedErrors
      };
    }
    
    // If data is in camelCase format, also add original field names for editing
    if (row.data.fullName && !row.data.FullName) {
      return {
        ...row,
        errors: normalizedErrors,
        data: {
          ...row.data,
          FullName: row.data.fullName,
          NPI: row.data.npi,
          TaxId: row.data.taxId,
          Specialties: Array.isArray(row.data.specialties) ? row.data.specialties.join(', ') : row.data.specialties,
          'Facility Group NPI': row.data.facilityGroupNpi || row.data.affiliation?.facilityId || '',
          'Facility Address': row.data.facilityAddress || row.data.affiliation?.facilityAddress || '',
          'Department Name': row.data.departmentName || row.data.affiliation?.departmentId || '',
          Roles: Array.isArray(row.data.roles) ? row.data.roles.join(', ') : (Array.isArray(row.data.affiliation?.role) ? row.data.affiliation.role.join(', ') : row.data.affiliation?.role || '')
        }
      };
    }
    
    return {
      ...row,
      errors: normalizedErrors
    };
  };

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const facilitiesData = await callAutomationService.getFacilities();
        setFacilities(facilitiesData || []);
      } catch (error) {
        console.error("Failed to fetch facilities", error);
        message.error("Failed to load facilities data.");
      }
    };
    fetchFacilities();
  }, []);

  useEffect(() => {
    if (validationData && validationData.response) {
      const results = validationData.response.results || [];
      const duplicateList = validationData.response.duplicates || [];
      
      // Normalize all rows to ensure consistent data structure
      const normalizedResults = results.map(normalizeRowData);

      // Sort the initial data to show invalid rows on top
      const initiallySortedData = [...normalizedResults].sort((a, b) => {
        const aIsInvalid = a.status === 'invalid';
        const bIsInvalid = b.status === 'invalid';
        if (aIsInvalid && !bIsInvalid) return -1;
        if (!aIsInvalid && bIsInvalid) return 1;
        return (a.rowIndex || 0) - (b.rowIndex || 0);
      });
      
      setTableData(initiallySortedData);
      setDuplicates(duplicateList);
      
      // Count errors
      const errors = normalizedResults.filter(row => row.status === 'invalid').length;
      setErrorCount(errors);
    }
  }, [validationData]);

  const handleAddRow = () => {
    const newRowIndex = tableData.length > 0 ? Math.max(...tableData.map(r => r.rowIndex)) + 1 : 1;

    const newRow = {
      rowIndex: newRowIndex,
      status: 'invalid',
      isNew: true,
      data: {
        'FullName': '', 'NPI': '', 'TaxId': '', 'Specialties': '',
        'Facility Group NPI': '', 'Facility Address': '',
        'Department Name': '', 'Roles': '',
        fullName: '', npi: '', taxId: '', specialties: [],
        facilityGroupNpi: '', facilityAddress: '',
        departmentName: '', roles: []
      },
      errors: [
        { field: 'FullName', message: 'FullName is required' },
        { field: 'NPI', message: 'NPI is required' },
        { field: 'TaxId', message: 'TaxId is required' },
        { field: 'Specialties', message: 'Specialties is required' },
        { field: 'Facility Group NPI', message: 'Facility Group NPI is required' },
        { field: 'Roles', message: 'Roles is required' },
      ],
      warnings: []
    };

    setTableData(prevData => [newRow, ...prevData]);
    setErrorCount(prevCount => prevCount + 1);
  };

  const handleDeleteRow = useCallback((rowIndexToDelete) => {
    const updatedData = tableData.filter(row => row.rowIndex !== rowIndexToDelete);
    setTableData(updatedData);

    const newErrorCount = updatedData.filter(row => row.status === 'invalid').length;
    setErrorCount(newErrorCount);
  }, [tableData]);

  // Pre-fetch departments for rows that already have a valid facility
  const preFetchDepartments = useCallback(async () => {
    if (tableData.length > 0 && facilities.length > 0) {
      for (const row of tableData) {
        if (fetchingDepartments.current.has(row.rowIndex)) {
          continue;
        }

        // We need getCellValue to correctly extract the NPI and Address
        const npi = getCellValue(row, { field: 'Facility Group NPI', validKey: 'facilityGroupNpi' });
        const address = getCellValue(row, { field: 'Facility Address', validKey: 'facilityAddress' });
        
        if (!npi) continue;

        const matchingNpiFacilities = facilities.filter(f => f.value === npi);
        if (matchingNpiFacilities.length === 0) continue;

        let foundFacility = null;
        if (matchingNpiFacilities.length === 1) {
          foundFacility = matchingNpiFacilities[0];
        } else if (address) {
          foundFacility = matchingNpiFacilities.find(f => {
            const facilityAddress = f.location ? formatFacilityAddress(f.location) : '';
            return facilityAddress.toLowerCase().trim() === address.toLowerCase().trim();
          });
        }

        if (foundFacility) {
          fetchingDepartments.current.add(row.rowIndex);
          
          setRowDepartments(prev => ({
            ...prev,
            [row.rowIndex]: { loading: true, departments: [] }
          }));
          
          try {
            const response = await getFacilityById(foundFacility.id);
            if (response.success && response.data && Array.isArray(response.data.departments)) {
              const activeDepartments = response.data.departments.filter(d => d.isActive);
              setRowDepartments(prev => ({
                ...prev,
                [row.rowIndex]: { loading: false, departments: activeDepartments }
              }));
            } else {
              setRowDepartments(prev => ({ ...prev, [row.rowIndex]: { loading: false, departments: [] } }));
            }
          } catch (error) {
            console.error(`Failed to pre-fetch departments for facility ${foundFacility.id}`, error);
            setRowDepartments(prev => ({ ...prev, [row.rowIndex]: { loading: false, departments: [] } }));
          }
        }
      }
    }
  }, [tableData, facilities]);

  useEffect(() => {
    preFetchDepartments();
  }, [preFetchDepartments]);

  const handleFacilityChange = useCallback(async (rowIndex, selectedOption) => {
    const updatedData = [...tableData];
    const rowIndexInArray = updatedData.findIndex(r => r.rowIndex === rowIndex);
    if (rowIndexInArray === -1) return;

    const row = updatedData[rowIndexInArray];
    
    // To prevent re-fetching from preFetchDepartments
    fetchingDepartments.current.delete(row.rowIndex);

    const fieldMapping = {
      'FullName': 'fullName',
      'NPI': 'npi',
      'TaxId': 'taxId',
      'Specialties': 'specialties',
      'Facility Group NPI': 'facilityGroupNpi',
      'Facility Address': 'facilityAddress',
      'Department Name': 'departmentName',
      'Roles': 'roles'
    };

    const updateRowField = (field, value) => {
      if (row.data) {
        row.data[field] = value;
        const camelCaseKey = fieldMapping[field];
        if (camelCaseKey) {
          if (field === 'Specialties' || field === 'Roles') {
            row.data[camelCaseKey] = value ? value.split(',').map(v => v.trim()).filter(v => v) : [];
          } else {
            row.data[camelCaseKey] = value;
          }
        }
      }
      
      const validationError = validateField(field, value, row.data);
      row.errors = row.errors.filter(err => err.field !== field);
      if (validationError) {
        row.errors.push({ field: field, message: validationError });
      }
    };
    
    if (selectedOption) {
      const facility = facilities.find(f => f.id === selectedOption.value);
      if (facility) {
        const address = facility.location ? formatFacilityAddress(facility.location) : '';
        
        updateRowField('Facility Group NPI', facility.value);
        updateRowField('Facility Address', address);
        updateRowField('Department Name', ''); // Clear department on new facility selection

        row.status = row.errors.length > 0 ? 'invalid' : 'valid';

        setTableData(updatedData);
        setErrorCount(updatedData.filter(r => r.status === 'invalid').length);
        
        setRowDepartments(prev => ({
          ...prev,
          [rowIndex]: { loading: true, departments: [] }
        }));
        
        try {
          const response = await getFacilityById(facility.id);
          if (response.success && response.data && Array.isArray(response.data.departments)) {
            const activeDepartments = response.data.departments.filter(d => d.isActive);
            setRowDepartments(prev => ({
              ...prev,
              [rowIndex]: { loading: false, departments: activeDepartments }
            }));
            if (activeDepartments.length === 0) {
              message.warn(`No active departments found for facility NPI: ${facility.value}`);
            }
          } else {
            setRowDepartments(prev => ({ ...prev, [rowIndex]: { loading: false, departments: [] } }));
          }
        } catch (error) {
          console.error(`Failed to fetch departments for facility ${facility.id}`, error);
          message.error("Failed to load departments.");
          setRowDepartments(prev => ({ ...prev, [rowIndex]: { loading: false, departments: [] } }));
        }
      }
    } else {
      updateRowField('Facility Group NPI', '');
      updateRowField('Facility Address', '');
      updateRowField('Department Name', '');

      row.status = row.errors.length > 0 ? 'invalid' : 'valid';
      
      setTableData(updatedData);
      setErrorCount(updatedData.filter(r => r.status === 'invalid').length);

      setRowDepartments(prev => ({
        ...prev,
        [rowIndex]: { loading: false, departments: [] }
      }));
    }
  }, [tableData, facilities]);

  // Validation functions
  const validateField = (field, value, rowData) => {
    // Optional fields
    const optionalFields = ['Department Name'];
    
    if (!value || value.trim() === '') {
      if (optionalFields.includes(field)) {
        return null; // Optional field, no error
      }
      return `${field} is required`;
    }

    switch (field) {
      case 'NPI':
        // Must be exactly 10 digits
        const npiDigits = value.replace(/\D/g, '');
        if (npiDigits.length !== 10) {
          return 'NPI must be exactly 10 digits';
        }
        break;
      
      case 'TaxId':
        // Must be exactly 9 digits
        const taxIdDigits = value.replace(/\D/g, '');
        if (taxIdDigits.length !== 9) {
          return 'Tax ID must be exactly 9 digits';
        }
        break;
      
      case 'Facility Group NPI':
        // Must be exactly 10 digits
        const facilityNpiDigits = value.replace(/\D/g, '');
        if (facilityNpiDigits.length !== 10) {
          return 'Facility Group NPI must be exactly 10 digits';
        }
        break;
      
      case 'FullName':
        if (value.trim().length < 2) {
          return 'Full Name must be at least 2 characters';
        }
        break;
      
      case 'Specialties':
        if (value.trim().length < 2) {
          return 'Specialties is required';
        }
        break;
      
      case 'Roles':
        const roles = value.toLowerCase().split(',').map(r => r.trim()).filter(r => r);
        const validRoles = ['practitioner', 'supervisor'];
        const invalidRoles = roles.filter(r => !validRoles.includes(r));
        if (invalidRoles.length > 0) {
          return `Invalid role(s): ${invalidRoles.join(', ')}. Valid roles are: practitioner, supervisor`;
        }
        if (roles.length === 0) {
          return 'At least one role is required';
        }
        break;
      
      case 'Facility Address':
        // Just needs to be a non-empty string if provided
        if (value.trim().length < 5) {
          return 'Facility Address must be at least 5 characters';
        }
        break;
      
      default:
        break;
    }

    return null; // No error
  };

  const handleCellEdit = useCallback((rowIndex, field, value) => {
    const updatedData = [...tableData];
    const rowIndexInArray = updatedData.findIndex(r => r.rowIndex === rowIndex);
    
    if (rowIndexInArray !== -1) {
      const row = updatedData[rowIndexInArray];
      
      // Validate input for NPI fields - only allow numbers
      if (field === 'NPI' || field === 'Facility Group NPI') {
        // Remove any non-numeric characters
        value = value.replace(/\D/g, '');
      }
      
      // Update the field value in both possible formats to maintain consistency
      if (row.data) {
        // Update original field name format
        row.data[field] = value;
        
        // Also update camelCase format if it exists
        const fieldMapping = {
          'FullName': 'fullName',
          'NPI': 'npi',
          'TaxId': 'taxId',
          'Specialties': 'specialties',
          'Facility Group NPI': 'facilityGroupNpi',
          'Facility Address': 'facilityAddress',
          'Department Name': 'departmentName',
          'Roles': 'roles'
        };
        
        const camelCaseKey = fieldMapping[field];
        if (camelCaseKey) {
          // Handle special cases for array fields
          if (field === 'Specialties' || field === 'Roles') {
            // Convert comma-separated string to array
            row.data[camelCaseKey] = value ? value.split(',').map(v => v.trim()).filter(v => v) : [];
          } else {
            // Top-level field
            row.data[camelCaseKey] = value;
          }
        }
      }
      
      // Validate the new value
      const validationError = validateField(field, value, row.data);
      
      // Initialize errors array if it doesn't exist
      if (!row.errors) {
        row.errors = [];
      }
      
      // Remove existing error for this field (errors are already normalized to display field names)
      row.errors = row.errors.filter(err => err.field !== field);
      
      // Add new error if validation failed
      if (validationError) {
        row.errors.push({
          field: field,
          message: validationError
        });
        row.status = 'invalid';
      } else {
        // If this field is now valid, check if there are any other errors
        if (row.errors.length === 0) {
          row.status = 'valid';
          // Keep isNew flag to keep it at the top until submission
        } else {
          // Still has other errors, keep as invalid
          row.status = 'invalid';
        }
      }
      
      setTableData(updatedData);
      
      // Recount errors
      const errors = updatedData.filter(r => r.status === 'invalid').length;
      setErrorCount(errors);
    }
  }, [tableData]);

  // Helper function to transform table data back to API format
  const transformRowToApiFormat = (row) => {
    if (!row || !row.data) return row;
    
    // Get values, preferring camelCase format if it exists, otherwise using display format
    const getData = (displayKey, camelKey, isArray = false) => {
      let value = row.data[camelKey] || row.data[displayKey] || '';
      
      // Convert comma-separated strings to arrays if needed
      if (isArray && typeof value === 'string') {
        return value ? value.split(',').map(v => v.trim()).filter(v => v) : [];
      }
      
      // If already an array, return as is
      if (isArray && Array.isArray(value)) {
        return value;
      }
      
      return value;
    };
    
    // Build the proper API structure
    const apiData = {
      fullName: getData('FullName', 'fullName'),
      npi: getData('NPI', 'npi'),
      taxId: getData('TaxId', 'taxId'),
      specialties: getData('Specialties', 'specialties', true),
      facilityGroupNpi: getData('Facility Group NPI', 'facilityGroupNpi') || row.data.affiliation?.facilityId || '',
      facilityAddress: getData('Facility Address', 'facilityAddress') || row.data.affiliation?.facilityAddress || '',
      departmentName: getData('Department Name', 'departmentName') || row.data.affiliation?.departmentId || '',
      roles: getData('Roles', 'roles', true).map(r => r.toLowerCase()),
      affiliation: {
        facilityId: row.data.affiliation?.facilityId || '',
        departmentId: row.data.affiliation?.departmentId || '',
        role: getData('Roles', 'roles', true).map(r => r.toLowerCase())
      }
    };
    
    // Return the row with proper API format
    return {
      rowIndex: row.rowIndex,
      status: row.status,
      data: apiData,
      errors: row.errors,
      warnings: row.warnings
    };
  };

  const getErrorForField = (row, field) => {
    if (row.errors && Array.isArray(row.errors)) {
      // First, check for field-specific error
      const fieldError = row.errors.find(err => err.field === field);
      if (fieldError) {
        return fieldError.message;
      }
      
      // If no field-specific error, check for general errors
      const generalError = row.errors.find(err => err.field === 'general' || err.field === 'General');
      if (generalError && row.status === 'invalid') {
        // Try to extract field-specific message from the general error
        const message = generalError.message;
        if (message && message.includes(field)) {
          // Extract the relevant part for this field
          const fieldPattern = new RegExp(`${field}\\n\\s+([^\\n]+(?:\\n\\s+For further information[^\\n]+)?)`);
          const match = message.match(fieldPattern);
          if (match) {
            return match[1].trim();
          }
        }
        
        // If can't extract specific message, return a simplified general error
        return 'This field has validation errors. Please check the data.';
      }
      
      return null;
    }
    return null;
  };

  const handleSubmit = async () => {
    if (errorCount > 0) {
      message.error('Please fix all errors before submitting');
      return;
    }

    setIsSubmitting(true);
    try {
      message.loading('Validating provider data...', 0);
      
      // Transform table data to proper API format before submitting
      const transformedResults = tableData.map(transformRowToApiFormat);
      
      // Prepare the validation data with transformed results and summary
      const submissionData = {
        summary: {
          totalRows: tableData.length,
          validRows: tableData.filter(row => row.status === 'valid').length,
          invalidRows: tableData.filter(row => row.status === 'invalid').length,
          duplicateNpis: duplicates.length
        },
        results: transformedResults
      };
      
      // Step 1: Revalidate before submitting
      const revalidationResponse = await revalidateProviderBulkData(submissionData);
      
      message.destroy();
      
      // Check if revalidation found any errors or duplicates
      if (revalidationResponse.statusCode === 'SUCCESS') {
        const { summary, results, duplicates } = revalidationResponse.response;
        
        // Update duplicates state for display (even if they don't block submission)
        setDuplicates(duplicates || []);
        
        // If there are still invalid rows, update the table and show errors
        if (summary.invalidRows > 0) {
          // Normalize and update table data with new validation results
          const normalizedResults = results.map(normalizeRowData);
          setTableData(normalizedResults);
          setErrorCount(summary.invalidRows);
          
          // Show error message
          let errorMessage = `Validation failed. ${summary.invalidRows} invalid row(s) found.`;
          if (duplicates && duplicates.length > 0) {
            errorMessage += ` ${summary.duplicateNpis} duplicate NPI(s) will be grouped together.`;
          }
          message.error(errorMessage);
          return;
        }
        
        // Validation passed, proceed with bulk create
        message.loading('Creating providers...', 0);
        const createResponse = await createProvidersFromBulkData(submissionData);
        message.destroy();
        
        if (createResponse.statusCode === 'SUCCESS') {
          const createSummary = createResponse.response.summary;
          message.success(`Successfully created ${createSummary.providersCreated} providers!`);
          if (onSuccess) {
            onSuccess(createResponse);
          }
        } else if (createResponse.statusCode === 'VALIDATION_FAILED') {
          // Handle validation failed response from bulk-create
          const validation = createResponse.response.validation;
          if (validation && validation.results) {
            const normalizedResults = validation.results.map(normalizeRowData);
            setTableData(normalizedResults);
            setDuplicates(validation.duplicates || []);
            setErrorCount(validation.summary.invalidRows);
          }
          message.error(createResponse.message || 'Validation failed. Fix errors and try again.');
        } else {
          message.error(createResponse.message || 'Failed to create providers. Please try again.');
        }
      } else {
        message.error('Validation failed. Please try again.');
      }
    } catch (error) {
      console.error('Error in provider submission:', error);
      message.destroy();
      
      // Check if error response contains validation errors
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.statusCode === 'VALIDATION_FAILED' && errorData.response.validation) {
          const validation = errorData.response.validation;
          if (validation.results) {
            const normalizedResults = validation.results.map(normalizeRowData);
            setTableData(normalizedResults);
            setDuplicates(validation.duplicates || []);
            setErrorCount(validation.summary.invalidRows);
          }
          message.error(errorData.message || 'Validation failed. Fix errors and try again.');
        } else {
          message.error(errorData.message || 'Failed to create providers. Please try again.');
        }
      } else {
        message.error('Failed to create providers. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    { field: 'FullName', label: 'Name', minWidth: 160, validKey: 'fullName' },
    { field: 'NPI', label: 'NPI Number', minWidth: 130, validKey: 'npi' },
    { field: 'TaxId', label: 'Tax ID', minWidth: 120, validKey: 'taxId' },
    { field: 'Specialties', label: 'Specialties', minWidth: 200, validKey: 'specialties' },
    { field: 'Facility Group NPI', label: 'Facility NPI', minWidth: 220, validKey: 'facilityGroupNpi' },
    { field: 'Facility Address', label: 'Facility Address', minWidth: 280, validKey: 'facilityAddress' },
    { field: 'Department Name', label: 'Department', minWidth: 150, validKey: 'departmentName' },
    { field: 'Roles', label: 'Roles', minWidth: 120, validKey: 'roles' },
    { field: 'actions', label: 'Actions', minWidth: 80, align: 'center' }
  ];

  const getCellValue = (row, col) => {
    // Try to get value from original field names first (for initial upload)
    let value = row.data[col.field];
    
    // If not found, try camelCase keys (for revalidation responses)
    if ((value === undefined || value === null) && col.validKey) {
      const keys = col.validKey.split('.');
      value = row.data;
      for (const key of keys) {
        value = value?.[key];
      }
    }
    
    // Convert arrays to comma-separated strings for display
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    return value || '';
  };
  const facilityOptions = useMemo(() => facilities.map(f => ({
    label: `${f.value} - ${f.facilityName}`, // NPI - FacilityName
    value: f.id, // unique ID
    npi: f.value,
  })), [facilities]);

  const addressOptions = useMemo(() => facilities
    .map(f => ({
      label: formatFacilityAddress(f.location),
      value: f.id,
      npi: f.value,
    }))
    .filter(f => f.label), [facilities]);

  return (
    <Box sx={{ 
      width: '100%', 
      pt: 3, 
      pb: 3,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3}}>
        <ThemedTooltip title="Go back" placement="bottom">
          <IconButton
            onClick={onBack}
            sx={{
              color: textPrimary,
              padding: '8px',
              '&:hover': { backgroundColor: isDark ? '#3e3b56' : '#f0f0f0' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </ThemedTooltip>
        <Typography 
          variant="h6" 
          sx={{ color: textPrimary, fontWeight: '600', fontSize: '20px' }}
        >
          Uploaded Provider Details
        </Typography>
      </Box>

      {/* Action/Error Bar */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: mainBg,
        paddingTop: '16px',
        paddingBottom: '16px',
        mb: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {errorCount > 0 ? (
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            backgroundColor: isDark ? 'rgba(230, 62, 43, 0.15)' : 'rgba(230, 62, 43, 0.08)',
            border: `1px solid ${brandOrange}`,
            borderRadius: '6px',
            padding: '10px 16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Typography sx={{ color: brandOrange, fontSize: '13px', fontWeight: '600' }}>
              {errorCount} error{errorCount > 1 ? 's' : ''} identified
            </Typography>
            <Typography sx={{ color: currentTheme?.text?.secondary, fontSize: '13px' }}>
              Make the following changes to submit provider details
            </Typography>
          </Box>
        ) : <Box />}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            onClick={handleAddRow}
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: '18px', color: '#FF6B47' }} />}
            sx={{
              backgroundColor: isDark ? 'transparent' : '#ffffff',
              color: '#FF6B47',
              borderColor: '#FF6B47',
              borderRadius: '4px',
              padding: '4px 12px',
              fontSize: '14px',
              fontWeight: '500',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 107, 71, 0.1)' : '#fff5f3',
                borderColor: '#FF6B47',
              }
            }}
          >
            Add Row
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={errorCount > 0 || isSubmitting}
            variant="contained"
            sx={{
              backgroundColor: '#FF6B47',
              color: '#ffffff',
              fontWeight: '500',
              textTransform: 'none',
              fontSize: '14px',
              padding: '4px 12px',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#FF5533',
              },
              '&.Mui-disabled': {
                backgroundColor: isDark ? '#4a4a4a' : '#d0d0d0',
              }
            }}
          >
            Add Providers
          </Button>
        </Box>
      </Box>

      {/* Duplicate NPI Warning */}
      {/* {duplicates && duplicates.length > 0 && (
        <Box sx={{ mb: 3}}>
          <Box sx={{
            backgroundColor: isDark ? 'rgba(255, 193, 7, 0.15)' : 'rgba(255, 193, 7, 0.08)',
            border: '1px solid #FFC107',
            borderRadius: '6px',
            padding: '12px 16px'
          }}>
            <Typography sx={{ color: '#FFC107', fontSize: '13px', fontWeight: '600', mb: 1 }}>
              ⚠️ Duplicate NPI(s) Detected
            </Typography>
            {duplicates.map((dup, idx) => (
              <Typography key={idx} sx={{ color: currentTheme?.text?.secondary, fontSize: '12px', ml: 2, mb: 0.5 }}>
                • NPI <strong>{dup.npi}</strong> found in row(s): {dup.rows.join(', ')} - {dup.message}
              </Typography>
            ))}
          </Box>
        </Box>
      )} */}

      {/* Table using Material-UI components */}
      <Paper sx={{ 
        width: '100%', 
        background: mainBg,
        borderRadius: '8px',
        marginTop: errorCount > 0 ? '0px' : '24px'
      }}>
        <TableContainer sx={{
          maxHeight: errorCount > 0 ? 'calc(65vh - 80px)' : '65vh',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(120,120,120,0.25) transparent',
          '&::-webkit-scrollbar': { width: 6, height: 6, background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(120,120,120,0.25)', borderRadius: 4 }
        }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col, colIdx) => (
                  <TableCell
                    key={col.field}
                    sx={{
                      minWidth: col.minWidth,
                      background: headerBg,
                      color: headerText,
                      fontWeight: 400,
                      borderBottom: cellBorder,
                      fontSize: '0.90rem',
                      minHeight: '38px',
                      height: '38px',
                      paddingTop: '4px',
                      paddingBottom: '4px'
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData
                .map((row, idx) => (
                  <MemoizedRow
                    key={row.rowIndex}
                    row={row}
                    columns={columns}
                    theme={{ isDark, textPrimary, brandOrange, cellBorder }}
                    rowDepartments={rowDepartments}
                    facilities={facilities}
                    facilityOptions={facilityOptions}
                    addressOptions={addressOptions}
                    handleCellEdit={handleCellEdit}
                    handleDeleteRow={handleDeleteRow}
                    handleFacilityChange={handleFacilityChange}
                    getCellValue={getCellValue}
                    getErrorForField={getErrorForField}
                    formatFacilityAddress={formatFacilityAddress}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
    </Box>
  );
};

export default BulkUploadValidationTable;
